import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { XIcon } from '@/components/icons';
import { getSignedMediaUrl, formatCurrencyForDisplay } from '@/lib/utils';
import GuestAddOnTooltip from './GuestAddOnTooltip';
import type { AddOnDetailModalProps } from '@/features/booking/types/ticketBookingTypes';


const AddOnDetailModal: React.FC<AddOnDetailModalProps & { currency?: string }> = ({
  isOpen,
  onClose,
  addOn,
  isSelected,
  onToggle,
  onQuantityChange,
  guestCount,
  remainingInventory,
  addOnIdsPerGuest,
  allAddOns,
  eventStartTime,
  earlyEntryDuration,
  timezone,
  currency,
}) => {
  // Format price using currency if provided, otherwise show raw amount
  const formatPrice = (amount: number) => currency 
    ? formatCurrencyForDisplay(amount, currency) 
    : `${(amount / 100).toFixed(2)}`;

  if (!isOpen || !addOn) return null;

  const hasValidImage = addOn.add_on.image_id && addOn.add_on.image_id !== '00000000-0000-0000-0000-000000000000';
  const imageUrl = hasValidImage ? getSignedMediaUrl(addOn.add_on.image_id, 'image').url : null;
  
  const isUnlimited = remainingInventory === null;
  const canApplyToAllGuests = isUnlimited || (remainingInventory as number) >= guestCount;
  const isInventoryLimited = !isUnlimited && (remainingInventory as number) < guestCount && (remainingInventory as number) > 0;
  const addOnId = addOn.add_on.id;
  const allowMultiple = addOn.add_on.allow_multiple_per_guest;

  const handleToggle = () => {
    if (guestCount > 0) {
      onToggle(addOnId, !isSelected);
    }
  };

  // Get total count of this addon across all guests
  const getAddOnCount = () => {
    return addOnIdsPerGuest.reduce((count, guestAddOns) => 
      count + guestAddOns.filter(id => id === addOnId).length, 0
    );
  };

  // Get quantity for a specific guest
  const getGuestQuantity = (guestIndex: number): number => {
    const guestAddOns = addOnIdsPerGuest[guestIndex] || [];
    return guestAddOns.filter(id => id === addOnId).length;
  };

  // Calculate remaining inventory for a specific guest (considering all other guests' selections)
  const getRemainingForGuest = (guestIndex: number): number | null => {
    if (isUnlimited) return null;
    let usedByOthers = 0;
    addOnIdsPerGuest.forEach((guestAddOns, idx) => {
      if (idx !== guestIndex) {
        usedByOthers += guestAddOns.filter(id => id === addOnId).length;
      }
    });
    return (remainingInventory as number) - usedByOthers;
  };

  const count = getAddOnCount();

  return (
    <motion.div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col transform transition-all"
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 pb-4 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-white">Add-on Details</h3>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <XIcon />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-visible scrollbar-hide">
          <div className="p-6 space-y-6" style={{ overflow: 'visible' }}>
            {/* Image */}
            <div className="relative h-64 rounded-xl overflow-hidden">
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: imageUrl 
                    ? `url(${imageUrl})` 
                    : 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>

            {/* Add-on Info */}
            <div className="space-y-4">
              <div>
                <h4 className="text-2xl font-bold text-white mb-2">{addOn.add_on.name}</h4>
                <p className="text-gray-300 leading-relaxed">{addOn.add_on.description}</p>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold text-blue-400">
                  {formatPrice(addOn.price)}
                </div>
                {isUnlimited ? (
                  <div className="text-sm text-gray-400">
                    Unlimited available
                  </div>
                ) : (remainingInventory as number) < 10 ? (
                  <div className="flex items-center gap-2 text-sm font-semibold text-red-400 bg-red-900/20 px-3 py-1.5 rounded">
                    <svg 
                      className="w-4 h-4" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12,6 12,12 16,14" />
                    </svg>
                    Hurry up! Only {remainingInventory} left
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">
                    {remainingInventory} available
                  </div>
                )}
              </div>
            </div>

            {/* Inventory Warning */}
            {isInventoryLimited && (
              <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="text-yellow-400 text-lg">⚠️</div>
                  <div>
                    <h5 className="text-yellow-400 font-semibold mb-1">Limited Inventory</h5>
                    <p className="text-yellow-200 text-sm">
                      Only {remainingInventory} available for {guestCount} guests. 
                      You can select this add-on but will need to manually assign it to specific guests in the selected add-ons section.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Early Entry Banner - Show when addon is "Early Entry" and is_platform_add_on is true */}
            {addOn.add_on.name === 'Early Entry' && addOn.add_on.is_platform_add_on && eventStartTime && earlyEntryDuration && (
              <div className="bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-500/50 rounded-lg p-4 shadow-lg">
                <div className="flex items-start space-x-3">

                  <div>
                    <h5 className="text-emerald-300 font-bold text-lg mb-1">You can take entry early!</h5>
                    <p className="text-emerald-100 text-sm">
                      Earliest Entry Time:{' '}
                      <span className="font-bold text-white">
                        {new Date(new Date(eventStartTime).getTime() - (earlyEntryDuration / 1_000_000)).toLocaleTimeString('en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit', 
                          hour12: true,
                          timeZone: timezone
                        })}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Selection Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
              <div>
                <div className="text-white font-semibold">
                  {isSelected ? 'Selected' : 'Add to Booking'}
                </div>
                <div className="text-sm text-gray-400">
                  {isSelected 
                    ? count > 0 
                      ? (
                          <span>
                            {count} qty × {formatPrice(addOn.price)} = <span className="text-blue-400 font-semibold">{formatPrice(addOn.price * count)}</span>
                          </span>
                        )
                      : 'Selected but not assigned to guests'
                    : canApplyToAllGuests 
                      ? `Will apply to all ${guestCount} guest${guestCount > 1 ? 's' : ''}`
                      : 'Manual assignment required'
                  }
                </div>
              </div>
              <button
                onClick={handleToggle}
                disabled={guestCount === 0}
                className={`w-14 h-7 rounded-full transition-colors duration-200 ${
                  guestCount === 0
                    ? 'bg-gray-600 cursor-not-allowed opacity-50'
                    : isSelected 
                    ? 'bg-blue-600' 
                    : 'bg-white/30 hover:bg-white/40'
                }`}
              >
                <div
                  className={`w-6 h-6 bg-white rounded-full transition-transform duration-200 ${
                    isSelected ? 'translate-x-7' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {/* Guest Selection UI - Only show when selected */}
            <AnimatePresence>
              {isSelected && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ 
                    duration: 0.4,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                  }}
                  style={{ overflow: 'visible' }}
                >
                  <div className="pt-4 border-t border-gray-800" style={{ overflow: 'visible' }}>
                    <div className="space-y-4" style={{ overflow: 'visible' }}>
                      <div style={{ overflow: 'visible' }}>
                        <h5 className="text-sm font-semibold text-gray-200 mb-3">
                          {allowMultiple ? 'Set Quantity per Guest' : 'Assign to Guests'}
                        </h5>
                        <div className={`grid gap-1.5 sm:gap-2 ${allowMultiple ? 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3' : 'grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8'}`} style={{ overflow: 'visible' }}>
                          {Array.from({ length: guestCount }, (_, guestIndex) => {
                            const guestAddOns = addOnIdsPerGuest[guestIndex] || [];
                            const hasAddOn = guestAddOns.includes(addOnId);
                            const quantity = getGuestQuantity(guestIndex);
                            const remaining = getRemainingForGuest(guestIndex);
                            const maxForGuest = remaining === null ? Infinity : remaining + quantity;
                            
                            return (
                              <motion.div
                                key={guestIndex}
                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                animate={{ 
                                  opacity: 1,
                                  y: 0,
                                  scale: 1
                                }}
                                transition={{ 
                                  duration: 0.2,
                                  delay: guestIndex * 0.03,
                                  ease: "easeOut"
                                }}
                                className="relative z-50"
                              >
                                {allowMultiple && onQuantityChange ? (
                                  /* Stepper UI for multiple quantity */
                                  <div className={`flex flex-col sm:flex-row items-center sm:justify-between p-2 sm:p-3 gap-2 sm:gap-1 rounded-lg border transition-colors ${
                                    quantity > 0
                                      ? 'bg-blue-600/20 border-blue-500/50'
                                      : 'bg-gray-800 border-gray-700'
                                  }`}>
                                    <div className="flex flex-col items-center sm:items-start text-center sm:text-left min-w-0 h-[34px] sm:h-auto justify-center sm:justify-start">
                                      <span className="text-xs sm:text-sm font-medium text-white truncate">G{guestIndex + 1}</span>
                                      {quantity > 0 ? (
                                        <span className="text-[10px] sm:text-xs text-blue-400 truncate">{formatPrice(addOn.price * quantity)}</span>
                                      ) : (
                                        <span className="text-[10px] sm:text-xs text-transparent truncate invisible">{formatPrice(addOn.price)}</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                                      <button
                                        onClick={() => onQuantityChange(addOnId, guestIndex, Math.max(0, quantity - 1))}
                                        disabled={quantity === 0}
                                        className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors ${
                                          quantity === 0
                                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                            : 'bg-gray-700 text-white hover:bg-gray-600'
                                        }`}
                                      >
                                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                        </svg>
                                      </button>
                                      <span className={`w-4 sm:w-6 text-center text-xs sm:text-base font-semibold ${quantity > 0 ? 'text-white' : 'text-gray-400'}`}>
                                        {quantity}
                                      </span>
                                      <button
                                        onClick={() => onQuantityChange(addOnId, guestIndex, quantity + 1)}
                                        disabled={quantity >= maxForGuest}
                                        className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors ${
                                          quantity >= maxForGuest
                                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                      >
                                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  /* Toggle UI for single quantity */
                                  <GuestAddOnTooltip
                                    guestIndex={guestIndex}
                                    guestAddOns={guestAddOns}
                                    allAddOns={allAddOns}
                                  >
                                    <button
                                      onClick={() => onToggle(addOnId, !hasAddOn, guestIndex)}
                                      className={`w-full py-1.5 sm:py-2 px-1 sm:px-3 rounded-lg text-[11px] sm:text-sm font-medium transition-colors border ${
                                        hasAddOn
                                          ? 'bg-blue-600 text-white border-blue-500 hover:bg-blue-700'
                                          : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700'
                                      }`}
                                    >
                                      G{guestIndex + 1}
                                    </button>
                                  </GuestAddOnTooltip>
                                )}
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AddOnDetailModal;
